import {
  StockProfitabilityFilters,
  StockProfitabilityResponse,
  StockProfitabilityRow,
  StockProfitabilitySummary,
} from 'types/models/StockProfitability';
import axiosInstance from './axios';
import joinURLParams from 'utils/joinUrlParams';

const parseStockProfitabilityRow = (item: any): StockProfitabilityRow => ({
  globalProductId: String(item?.global_product_id ?? item?.globalProductId ?? ''),
  name: item?.name ?? '',
  sku: item?.sku ?? '',
  orders: Number(item?.orders ?? 0),
  salesUnits: Number(item?.sales_units ?? 0),
  averageCost: Number(item?.average_cost ?? 0),
  salePrice: Number(item?.sale_price ?? 0),
  replacementCost: Number(item?.replacement_cost ?? 0),
  profitabilityAmount: item?.profitability_amount == null ? null : Number(item.profitability_amount),
  profitabilityPercent: item?.profitability_percent == null ? null : Number(item.profitability_percent),
  lossesCost: Number(item?.losses_cost ?? 0),
  realProfitabilityAmount: item?.real_profitability_amount == null ? null : Number(item.real_profitability_amount),
  realProfitabilityPercent: item?.real_profitability_percent == null ? null : Number(item.real_profitability_percent),
});

const parseStockProfitabilityResponse = (data: any): StockProfitabilityResponse => {
  const rawProducts = Array.isArray(data?.products) ? data.products : [];
  const products = rawProducts.map(parseStockProfitabilityRow);
  const totals = data?.totals ?? {};
  const pagination = data?.pagination ?? {};

  return {
    products,
    totals: {
      orders: Number(totals?.orders ?? 0),
      salesUnits: Number(totals?.sales_units ?? 0),
      profitabilityAmount: Number(totals?.profitability_amount ?? 0),
      profitabilityPercent: totals?.profitability_percent == null ? null : Number(totals.profitability_percent),
      realProfitabilityAmount: Number(totals?.real_profitability_amount ?? 0),
      realProfitabilityPercent:
        totals?.real_profitability_percent == null ? null : Number(totals.real_profitability_percent),
    },
    pagination: {
      currentPage: Number(pagination?.current_page ?? 1),
      perPage: Number(pagination?.per_page ?? products.length),
      total: Number(pagination?.total ?? products.length),
    },
  };
};

const parseStockProfitabilitySummary = (data: any): StockProfitabilitySummary => ({
  totalSalesPrice: Number(data?.total_sales_price ?? 0),
  periodLosses: Number(data?.period_losses ?? 0),
  periodLossesCost: Number(data?.period_losses_cost ?? 0),
});

export const getStockProfitability = async (
  filters: StockProfitabilityFilters,
  page = 1,
  perPage = 10,
): Promise<StockProfitabilityResponse> => {
  try {
    const url = joinURLParams({ page, perPage }, 'stock/reporting/profitability');
    const { data } = await axiosInstance({
      method: 'post',
      url,
      data: {
        venueId: filters.venueId,
        commerceIds: filters.commerceIds,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        includeWarehouseLosses: filters.includeWarehouseLosses,
      },
    });
    return parseStockProfitabilityResponse(data);
  } catch (error) {
    throw error;
  }
};

// Tarjetas resumen (ventas y mermas del período). Endpoint separado de la tabla.
export const getStockProfitabilitySummary = async (
  filters: StockProfitabilityFilters,
): Promise<StockProfitabilitySummary> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: 'stock/reporting/profitability/summary',
      data: {
        venueId: filters.venueId,
        commerceIds: filters.commerceIds,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        includeWarehouseLosses: filters.includeWarehouseLosses,
      },
    });
    return parseStockProfitabilitySummary(data);
  } catch (error) {
    throw error;
  }
};

export const exportStockProfitability = async (filters: StockProfitabilityFilters): Promise<Blob | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: 'stock/reporting/profitability/export',
      data: {
        venueId: filters.venueId,
        commerceIds: filters.commerceIds,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        includeWarehouseLosses: filters.includeWarehouseLosses,
      },
      responseType: 'blob',
    });
    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};
